import { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { Leaf, Droplets, Shield, Sun } from "lucide-react";

const features = [
  {
    icon: Leaf,
    title: "Herbal Infused",
    description: "Made with powerful herbs and nutrient-rich oils.",
  },
  {
    icon: Droplets,
    title: "Scalp Nourishing",
    description: "Helps support a healthier scalp environment.",
  },
  {
    icon: Shield,
    title: "Moisture Sealing",
    description: "Locks in moisture and helps protect dry hair.",
  },
  {
    icon: Sun,
    title: "Tropical Luxury Feel",
    description: "A self-care experience inspired by nature and island beauty.",
  },
];

const FeatureCard = ({ feature, index }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay: index * 0.15 }}
      className="text-center p-8 group"
      data-testid={`feature-${index}`}
    >
      <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-brand-deepGreen/5 mb-6 group-hover:bg-brand-deepGreen/10 transition-colors duration-500">
        <feature.icon
          className="w-7 h-7 text-brand-deepGreen"
          strokeWidth={1}
        />
      </div>
      <h3 className="font-heading text-xl font-light text-brand-deepGreen mb-3">
        {feature.title}
      </h3>
      <p className="text-brand-coconutBrown/70 text-sm font-body font-light leading-relaxed">
        {feature.description}
      </p>
    </motion.div>
  );
};

const WhySection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section
      ref={ref}
      className="py-24 md:py-32 bg-brand-cream relative overflow-hidden"
      data-testid="why-section"
    >
      {/* Decorative Elements */}
      <div className="absolute top-1/2 left-0 w-72 h-72 bg-brand-warmGold/5 rounded-full blur-3xl -translate-y-1/2" />
      <div className="absolute top-1/2 right-0 w-72 h-72 bg-brand-deepGreen/5 rounded-full blur-3xl -translate-y-1/2" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-12">
        {/* Section Header */}
        <div className="text-center mb-16">
          <motion.span
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="text-brand-warmGold text-xs tracking-[0.3em] uppercase font-body block mb-4"
          >
            The Difference
          </motion.span>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="font-heading text-4xl sm:text-5xl font-light text-brand-deepGreen mb-4"
          >
            Why Pure Intentional?
          </motion.h2>
          <motion.div
            initial={{ opacity: 0, scaleX: 0 }}
            animate={isInView ? { opacity: 1, scaleX: 1 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="section-divider mx-auto"
          />
        </div>

        {/* Features Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-8">
          {features.map((feature, index) => (
            <FeatureCard key={feature.title} feature={feature} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default WhySection;
